import { CheckCircle2, Clock3, Code2, Copy, Download, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '~/components/common/Button'
import { LoadingState } from '~/components/common/LoadingSpinner'
import DocMarkdown from '~/components/documents/DocMarkdown'
import {
  LandingTaskPanelBody,
  LandingTaskPanelFooter,
  LandingTaskPanelFrame,
  LandingTaskPanelHeader,
  LandingTaskPanelNotice,
  LandingTaskPanelTab,
  LandingTaskPanelTabs,
  LandingTaskStatusPill
} from '~/components/task/LandingTaskPanelShell'
import LandingTaskProgressTimeline from '~/components/task/LandingTaskProgressTimeline'
import {
  RIGHT_PANEL_MARKDOWN_CLASS,
  RightPanelHorizontalScroll
} from '~/components/task/RightPanelHorizontalScroll'
import { TaskStatuses } from '~/constants/task-status'
import { getCodeDeltaContent } from '~/hooks/task/useCodeDeltaContent'
import { useTaskDetails } from '~/hooks/task/useTaskDetails'
import { useTaskProgressMessages } from '~/hooks/task/useTaskProgressMessages'
import {
  AgentTaskStatus,
  agentTaskStatusFromJSON,
  CommandType,
  TaskStatusAndRequestResponseTuple
} from '~/protos/agents'
import { TaskObjectWithNoDetails } from '~/types/TaskType'
import { getEffectiveTaskStatus, normalizeCommandType } from '~/task/commandTypeUtils'
import { BetterToast } from '~/utils/toast'

type LandingDataWeaveTab = 'progress' | 'output'

interface LandingDataWeaveTaskPanelProps {
  conversationUuid: string
  onClose?: () => void
  openRequestId?: number
  task: TaskObjectWithNoDetails
}

const LANDING_DATAWEAVE_COMMANDS: ReadonlySet<CommandType> = new Set([
  CommandType.GENERATE_DATAWEAVE,
  CommandType.GENERATE_DATAWEAVE_REPO_AWARE,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE,
  CommandType.DATAWEAVE_TO_DATA_CREATION,
  CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE
])

function formatStatus(taskStatus: AgentTaskStatus) {
  const config = TaskStatuses[taskStatus]
  if (config?.label) return config.label
  switch (config?.group) {
    case 'IN_PROGRESS':
      return 'Running'
    case 'COMPLETED':
      return 'Completed'
    case 'ERRORED':
      return 'Errored'
    case 'STOPPED':
      return 'Stopped'
    default:
      return 'Task'
  }
}

function statusGroupForTaskStatus(taskStatus: AgentTaskStatus) {
  return TaskStatuses[taskStatus]?.group
}

function extractDataWeaveOutput(tuple: TaskStatusAndRequestResponseTuple): string {
  const direct = tuple.response?.[0]?.responseContentStream?.document?.dwlMd || ''
  if (direct.trim()) return direct
  return getCodeDeltaContent(tuple)
}

function findDataWeaveOutput(tuples: TaskStatusAndRequestResponseTuple[]): string {
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const content = extractDataWeaveOutput(tuples[i])
    if (content.trim()) return content
  }
  return ''
}

function extractDataWeaveCode(markdown: string): string {
  const matches = [...markdown.matchAll(/```(?:dataweave|dwl)?\s*([\s\S]*?)```/gi)]
  const latest = matches[matches.length - 1]?.[1]
  return latest?.trim() || ''
}

function extractLeadMarkdown(markdown: string): string {
  return markdown.replace(/```(?:dataweave|dwl)?\s*[\s\S]*?```/gi, '').trim()
}

function latestJsonMetadata(tuples: TaskStatusAndRequestResponseTuple[]): Record<string, unknown> {
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const metadata = tuples[i].response?.[0]?.responseContentStream?.jsonMetadata
    if (!metadata) continue
    try {
      return JSON.parse(metadata)
    } catch {
      return {}
    }
  }
  return {}
}

function downloadText(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

export function isLandingDataWeaveTask(task: TaskObjectWithNoDetails | undefined): boolean {
  if (!task) return false
  return LANDING_DATAWEAVE_COMMANDS.has(normalizeCommandType(task.cmd_type))
}

export default function LandingDataWeaveTaskPanel({
  conversationUuid,
  onClose,
  openRequestId,
  task
}: LandingDataWeaveTaskPanelProps) {
  const taskId = task.task_id
  const fallbackStatus = agentTaskStatusFromJSON(task.status)
  const taskDetailsQuery = useTaskDetails(conversationUuid, String(taskId))
  const {
    data: taskDetails,
    isLoading: taskDetailsLoading,
    refetch: refetchTaskDetails
  } = taskDetailsQuery

  const tuples = useMemo(
    () =>
      (taskDetails?.task?.details?.orderedStateResponses ??
        []) as TaskStatusAndRequestResponseTuple[],
    [taskDetails?.task?.details?.orderedStateResponses]
  )
  const detailStatus = taskDetails?.task?.status
    ? agentTaskStatusFromJSON(taskDetails.task.status)
    : AgentTaskStatus.NONE_TASK_STATUS
  const rawTaskStatus =
    detailStatus !== AgentTaskStatus.NONE_TASK_STATUS ? detailStatus : fallbackStatus
  const taskStatus = getEffectiveTaskStatus(rawTaskStatus, normalizeCommandType(task.cmd_type))
  const statusGroup = TaskStatuses[taskStatus]?.group
  const isRunning = statusGroup === 'IN_PROGRESS'
  const { data: progressMessages = [] } = useTaskProgressMessages(
    conversationUuid,
    String(taskId),
    {
      enabled: !!conversationUuid && !!taskId,
      refetchInterval: isRunning ? 10000 : false
    }
  )
  const outputMarkdown = useMemo(() => findDataWeaveOutput(tuples), [tuples])
  const dataWeaveCode = useMemo(() => extractDataWeaveCode(outputMarkdown), [outputMarkdown])
  const leadMarkdown = useMemo(() => extractLeadMarkdown(outputMarkdown), [outputMarkdown])
  const metadata = useMemo(() => latestJsonMetadata(tuples), [tuples])
  const defaultTab: LandingDataWeaveTab = !isRunning && outputMarkdown ? 'output' : 'progress'
  const [activeTab, setActiveTab] = useState<LandingDataWeaveTab>(defaultTab)
  const effectiveActiveTab: LandingDataWeaveTab = isRunning ? activeTab : 'output'
  const outputAutoSelectedRef = useRef(false)

  useEffect(() => {
    outputAutoSelectedRef.current = false
    setActiveTab(defaultTab)
  }, [taskId, openRequestId, defaultTab])

  useEffect(() => {
    if (isRunning) {
      outputAutoSelectedRef.current = false
      setActiveTab('progress')
      return
    }
    if (outputMarkdown && !outputAutoSelectedRef.current) {
      outputAutoSelectedRef.current = true
      setActiveTab('output')
    }
  }, [isRunning, outputMarkdown])

  useEffect(() => {
    refetchTaskDetails()
  }, [task.status, task.updated_ts, refetchTaskDetails])

  useEffect(() => {
    if (!isRunning) return
    const intervalId = window.setInterval(() => {
      refetchTaskDetails()
    }, 5000)
    return () => window.clearInterval(intervalId)
  }, [isRunning, refetchTaskDetails])

  const copyOutput = useCallback(async () => {
    const content = dataWeaveCode || outputMarkdown
    if (!content) return
    await navigator.clipboard?.writeText(content)
    BetterToast.success('DataWeave copied')
  }, [dataWeaveCode, outputMarkdown])

  const downloadOutput = useCallback(() => {
    const content = dataWeaveCode || outputMarkdown
    if (!content) return
    downloadText(`dataweave-task-${taskId}.${dataWeaveCode ? 'dwl' : 'md'}`, content)
  }, [dataWeaveCode, outputMarkdown, taskId])

  const tabs: Array<LandingTaskPanelTab<LandingDataWeaveTab>> = [
    ...(isRunning
      ? [
          {
            id: 'progress' as const,
            label: 'Progress',
            icon: <Clock3 className='h-4 w-4' />
          }
        ]
      : []),
    {
      id: 'output',
      label: 'DataWeave',
      icon: <Code2 className='h-4 w-4' />,
      disabled: !outputMarkdown
    }
  ]

  const tabContent = () => {
    if (!taskDetails && taskDetailsLoading) {
      return <LoadingState message='Loading DataWeave task...' className='h-full' />
    }

    if (effectiveActiveTab === 'progress') {
      return (
        <RightPanelHorizontalScroll testId='landing-dataweave-progress-scroll'>
          <LandingTaskProgressTimeline
            emptyDescription='The DataWeave agent has started, but no progress checkpoints have arrived yet.'
            emptyTitle={isRunning ? 'Waiting for DataWeave progress' : 'No DataWeave progress yet'}
            progressMessages={progressMessages}
            isRunning={isRunning}
          />
        </RightPanelHorizontalScroll>
      )
    }

    return outputMarkdown ? (
      <RightPanelHorizontalScroll className='p-4' testId='landing-dataweave-output-scroll'>
        <div className='min-w-[680px] space-y-4'>
          <div className='flex flex-wrap items-center gap-2'>
            {metadata.compile_passed !== undefined && (
              <span
                className={
                  metadata.compile_passed
                    ? 'rounded-full border border-tokens-status-success-2 bg-tokens-status-success-3 px-2.5 py-1 text-xs font-semibold text-tokens-status-success-1'
                    : 'rounded-full border border-tokens-status-warning-30 bg-tokens-status-warning-3 px-2.5 py-1 text-xs font-semibold text-tokens-status-warning-1'
                }
              >
                {metadata.compile_passed ? 'Runs successfully' : 'Runtime issues'}
              </span>
            )}
            {metadata.output_match !== undefined && (
              <span
                className={
                  metadata.output_match
                    ? 'rounded-full border border-tokens-status-success-2 bg-tokens-status-success-3 px-2.5 py-1 text-xs font-semibold text-tokens-status-success-1'
                    : 'rounded-full border border-tokens-status-warning-30 bg-tokens-status-warning-3 px-2.5 py-1 text-xs font-semibold text-tokens-status-warning-1'
                }
              >
                {metadata.output_match ? 'Output matched' : 'Needs review'}
              </span>
            )}
          </div>

          {leadMarkdown && (
            <DocMarkdown text={leadMarkdown} className={RIGHT_PANEL_MARKDOWN_CLASS} />
          )}

          {dataWeaveCode ? (
            <div className='overflow-hidden rounded-2xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white'>
              <div className='flex items-center justify-between border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 px-3 py-2 text-xs font-semibold text-tokens-neutral-text-secondary'>
                <span className='uppercase'>dataweave</span>
                <Button
                  variant='borderless'
                  onClick={copyOutput}
                  className='h-7 text-tokens-neutral-text-secondary hover:text-tokens-neutral-text-primary'
                >
                  <Copy className='h-3.5 w-3.5' />
                  Copy
                </Button>
              </div>
              <pre className='max-h-[calc(100vh-360px)] min-h-[220px] overflow-auto bg-tokens-neutral-bg-white p-4 text-sm leading-6 text-tokens-neutral-text-primary'>
                <code>{dataWeaveCode}</code>
              </pre>
            </div>
          ) : (
            <DocMarkdown text={outputMarkdown} className={RIGHT_PANEL_MARKDOWN_CLASS} />
          )}
        </div>
      </RightPanelHorizontalScroll>
    ) : (
      <div className='flex h-full items-center justify-center p-6 text-center text-tokens-neutral-text-tertiary'>
        Generated DataWeave will appear here when the task finishes.
      </div>
    )
  }

  return (
    <LandingTaskPanelFrame>
      <LandingTaskPanelHeader
        onClose={onClose}
        title={`DataWeave task #${taskId}`}
        subtitle='Input/output example transformation'
        actions={
          <>
            {outputMarkdown && (
              <Button
                variant='outlined'
                onClick={downloadOutput}
                className='h-8 rounded-md px-2.5 text-xs font-semibold'
              >
                <Download className='h-3.5 w-3.5' />
                Download
              </Button>
            )}
            <LandingTaskStatusPill
              group={statusGroupForTaskStatus(taskStatus)}
              label={formatStatus(taskStatus)}
            />
          </>
        }
      />

      {isRunning && (
        <LandingTaskPanelTabs activeTab={effectiveActiveTab} onChange={setActiveTab} tabs={tabs} />
      )}

      <LandingTaskPanelBody>{tabContent()}</LandingTaskPanelBody>

      <LandingTaskPanelFooter>
        {outputMarkdown ? (
          <div className='flex items-center gap-2 text-sm text-tokens-neutral-text-secondary'>
            <CheckCircle2 className='h-4 w-4 shrink-0 text-tokens-status-success-1' />
            <span>Send DataWeave feedback in chat. It will be routed to task #{taskId}.</span>
          </div>
        ) : isRunning ? (
          <LandingTaskPanelNotice icon={<Loader2 className='h-4 w-4 shrink-0 animate-spin' />}>
            DataWeave generation is running.
          </LandingTaskPanelNotice>
        ) : (
          <div className='text-sm text-tokens-neutral-text-secondary'>
            DataWeave output is not available yet.
          </div>
        )}
      </LandingTaskPanelFooter>
    </LandingTaskPanelFrame>
  )
}
